// Kataloğa yükselt penceresi (spec §5 PromoteDialog — DR9/DR11/DR12/DR15/DR17/DR19). Müşteriye özel
// göstergeyi (ve formüllerinin zorunlu bağımlılıklarını) ortak kataloğa taşır. Üstte Katalog adı +
// Yeni kod + Üst kategori (SearchSelect, yalnız ortak kategoriler); "Paketlere ekle" onay kutuları;
// açılışta ve her değişiklikte 400 ms gecikmeli önizleme ("Denetleniyor…" aria-live, beklerken
// Yükselt pasif; sıra dışı yanıt jetonla elenir); zincir tablosu / engel paneli (role=alert);
// Önizleme sayıları; onay ConfirmDialog → yükseltme. 409 sonrası odak hatalı satırın kod girdisine.
// Odak tuzağı + Esc + kapanışta odağın tetikleyiciye dönüşü KpiModal'dan (DR17-5).

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { type KpiMetric, type KpiPackage, type KpiPromotePreview } from "@mahir/shared";
import { promoteKpiMetric, type KpiPromoteResult } from "../../api/kpi";
import { useMediaQuery } from "../../hooks/use-media-query";
import {
  KPI_PACKAGES_COPY,
  isPackageErrorCode,
  packageErrorText,
  packageNamesText,
} from "../../lib/kpi-packages-copy";
import {
  applySuggestions,
  classifyBlockers,
  confirmText,
  createCheckScheduler,
  errorChain,
  type CheckScheduler,
  initialPromoteForm,
  chainDefaultCodes,
  mergeRowMessages,
  parentProblemMessages,
  placeRowErrors,
  isApiErrorLike,
  nameWarningFor,
  rowErrors,
  splitChain,
  toPromoteBody,
  type PlacedRowErrors,
  type PromoteChainOverride,
  type PromoteChainRow,
  type PromoteFormState,
} from "../../lib/kpi-promote-form";
import { useKpiPackagesStore } from "../../stores/kpi-packages-store";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { SearchSelect, type SearchSelectOption } from "../common/SearchSelect";
import { KpiModal } from "./KpiModal";
import {
  BreakdownSummary,
  PromoteChainTable,
  blockEnter,
  promoteBreakdownId,
  promoteCodeInputId,
  promoteRowErrorId,
} from "./PromoteChainTable";
import {
  amberBox,
  errorBox,
  fieldStyle,
  footerBtn,
  inputStyle,
  labelStyle,
  monoStyle,
} from "./kpi-form-styles";

const P = KPI_PACKAGES_COPY.promote;

export interface PromoteDialogProps {
  open: boolean;
  /** yükseltilecek müşteriye özel gösterge */
  metric: Pick<KpiMetric, "id" | "label" | "code">;
  /** katalog (ortak + bu müşterinin) — üst kategori seçenekleri */
  catalog: readonly KpiMetric[];
  onClose: () => void;
  onPromoted: (result: KpiPromoteResult, packageIds: string[]) => void;
}

/** Ortak (global) ∧ saklı `category` düğümler — ağaç sırası + girinti. */
export function globalCategoryOptions(catalog: readonly KpiMetric[]): SearchSelectOption[] {
  const cats = catalog.filter(
    (m) => m.clientId === null && m.nodeKind === "category" && m.status !== "deprecated",
  );
  const ids = new Set(cats.map((c) => c.id));
  const kids = new Map<string | null, KpiMetric[]>();
  for (const c of cats) {
    const p = c.parentId && ids.has(c.parentId) ? c.parentId : null;
    kids.set(p, [...(kids.get(p) ?? []), c]);
  }
  const out: SearchSelectOption[] = [];
  const walk = (parent: string | null, depth: number): void => {
    const list = [...(kids.get(parent) ?? [])].sort(
      (a, b) => a.orderIndex - b.orderIndex || a.label.localeCompare(b.label, "tr"),
    );
    for (const c of list) {
      out.push({ value: c.id, label: c.label, code: c.code, depth });
      if (depth < 8) walk(c.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

export type PromoteCheck =
  | { status: "checking" }
  | { status: "ok"; preview: KpiPromotePreview }
  | { status: "error"; error: unknown };

const sectionTitle: CSSProperties = {
  fontSize: "1rem",
  fontWeight: 600,
  color: "var(--color-text)",
  margin: "16px 0 8px",
};
const muted: CSSProperties = { fontSize: "0.875rem", color: "var(--color-text-3)" };

/** "satıra git" hedefinin DOM kimliği (kod girdisi ya da kırılım özet satırı). */
function focusTargetId(t: PlacedRowErrors["order"][number]): string {
  return t.kind === "row" ? promoteCodeInputId(t.id) : promoteBreakdownId(t.id);
}

/** Hata → genel satır metni (paket/promote kodu kopyası, yoksa sunucu metni). */
function errorText(error: unknown): string {
  if (!isApiErrorLike(error)) return KPI_PACKAGES_COPY.errorFallback;
  return isPackageErrorCode(error.code)
    ? packageErrorText(error.code, error.message, error.details)
    : error.message;
}

export function PromoteDialog(props: PromoteDialogProps): JSX.Element | null {
  const { open, metric, catalog } = props;
  const narrow = useMediaQuery("(max-width: 720px)");
  const packages = useKpiPackagesStore((s) => s.packages);
  const activePackages = useMemo(() => packages.filter((p) => p.status === "active"), [packages]);
  const categoryOptions = useMemo(() => globalCategoryOptions(catalog), [catalog]);

  const [form, setForm] = useState<PromoteFormState>(() => initialPromoteForm(metric));
  const [check, setCheck] = useState<PromoteCheck>({ status: "checking" });
  /** son çizilen zincir — 409 ayrıntısından da gelir (tablo hatalı satırla yine görünür) */
  const [chain, setChain] = useState<PromoteChainRow[]>([]);
  /** son başarılı önizleme — denetim sürerken sayılar/uyarılar titremesin */
  const [lastPreview, setLastPreview] = useState<KpiPromotePreview | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [focusReq, setFocusReq] = useState(0);
  /** genel yükseltme hatası (ağ/sunucu) — önizleme korunur, Yükselt yeniden denetimle açılır */
  const [commitError, setCommitError] = useState<unknown>(null);
  const formRef = useRef(form);
  formRef.current = form;
  const defaults = useMemo(() => chainDefaultCodes(chain), [chain]);
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  const scheduler = useRef<CheckScheduler | null>(null);
  scheduler.current ??= createCheckScheduler((token) => {
    const isCurrent = (): boolean => scheduler.current?.isCurrent(token) === true;
    const body = {
      ...toPromoteBody(formRef.current, true, defaultsRef.current),
      dryRun: true as const,
    };
    promoteKpiMetric(metric.id, body).then(
      (preview) => {
        if (!isCurrent()) return; // sıra dışı (bayat) yanıt
        setCheck({ status: "ok", preview });
        setLastPreview(preview);
        setChain(preview.chain);
        setForm((f) => applySuggestions(f, preview));
      },
      (error: unknown) => {
        if (!isCurrent()) return;
        setCheck({ status: "error", error });
        const c = errorChain(error);
        if (c) setChain(c);
      },
    );
  });
  const sched = scheduler.current;

  useEffect(() => {
    if (open) void useKpiPackagesStore.getState().loadPackages();
  }, [open]);

  // açılışta gecikmesiz, sonra her alan değişiminde 400 ms (DR9)
  const bodyKey = JSON.stringify(toPromoteBody(form, true, defaults));
  const firstCheck = useRef(true);
  useEffect(() => {
    if (!open) return;
    setCheck({ status: "checking" });
    setCommitError(null);
    sched.schedule(firstCheck.current ? 0 : undefined);
    firstCheck.current = false;
  }, [open, bodyKey]);
  useEffect(() => () => sched.cancel(), [sched]);

  useEffect(() => {
    if (focusReq === 0) return;
    const err = check.status === "error" ? check.error : null;
    const first = placeRowErrors(rowErrors(err), metric.id, chain).order[0];
    if (first) document.getElementById(focusTargetId(first))?.focus();
    // yalnız odak isteğinde (409 sonrası / "satıra git") — durum bağımlılık değil
  }, [focusReq]);

  const patch = (p: Partial<PromoteFormState>): void => setForm((f) => ({ ...f, ...p }));
  const patchRow = (id: string, p: PromoteChainOverride): void =>
    setForm((f) => ({
      ...f,
      chainOverrides: { ...f.chainOverrides, [id]: { ...f.chainOverrides[id], ...p } },
    }));

  async function doPromote(): Promise<void> {
    setPromoting(true);
    setCommitError(null);
    try {
      const body = toPromoteBody(formRef.current, false, defaultsRef.current);
      const result = await promoteKpiMetric(metric.id, body);
      setConfirmOpen(false);
      if (body.packageIds?.length) useKpiPackagesStore.getState().invalidatePackages();
      props.onPromoted(result, body.packageIds ?? []);
    } catch (e) {
      setConfirmOpen(false);
      const hasGlobalCategory = categoryOptions.length > 0;
      if (rowErrors(e).size === 0 && classifyBlockers(e, { hasGlobalCategory }) === null) {
        // ağ/sunucu hatası: önizleme korunur, hata gösterilir, yeniden denetim Yükselt'i açar
        setCommitError(e);
        setCheck({ status: "checking" });
        sched.schedule(0);
        return;
      }
      sched.cancel(); // uçuştaki önizleme hatayı ezmesin
      setCheck({ status: "error", error: e });
      const c = errorChain(e);
      if (c) setChain(c);
      setFocusReq((n) => n + 1);
    } finally {
      setPromoting(false);
    }
  }

  return (
    <PromoteDialogView
      open={open}
      metric={metric}
      catalog={catalog}
      narrow={narrow}
      packages={activePackages}
      categoryOptions={categoryOptions}
      form={form}
      check={check}
      lastPreview={lastPreview}
      commitError={commitError}
      chain={chain}
      confirmOpen={confirmOpen}
      promoting={promoting}
      onPatch={patch}
      onPatchRow={patchRow}
      onGoToRow={() => setFocusReq((n) => n + 1)}
      onOpenConfirm={() => setConfirmOpen(true)}
      onConfirm={() => void doPromote()}
      onCancelConfirm={() => setConfirmOpen(false)}
      onClose={props.onClose}
    />
  );
}

export interface PromoteDialogViewProps {
  open: boolean;
  metric: PromoteDialogProps["metric"];
  catalog: readonly KpiMetric[];
  narrow: boolean;
  /** etkin paketler */
  packages: readonly KpiPackage[];
  categoryOptions: readonly SearchSelectOption[];
  form: PromoteFormState;
  check: PromoteCheck;
  lastPreview: KpiPromotePreview | null;
  /** genel yükseltme hatası (önizleme ve Yükselt açık kalır) */
  commitError?: unknown;
  chain: readonly PromoteChainRow[];
  confirmOpen: boolean;
  promoting: boolean;
  onPatch: (p: Partial<PromoteFormState>) => void;
  onPatchRow: (metricId: string, p: PromoteChainOverride) => void;
  onGoToRow: () => void;
  onOpenConfirm: () => void;
  onConfirm: () => void;
  onCancelConfirm: () => void;
  onClose: () => void;
}

/** Görünüm (durum dışarıdan — SSR testleri doğrudan verir). */
export function PromoteDialogView(props: PromoteDialogViewProps): JSX.Element | null {
  const { open, metric, catalog, narrow, form, check, lastPreview, chain, promoting } = props;
  const activePackages = props.packages;
  const categoryOptions = props.categoryOptions;
  const hasGlobalCategory = categoryOptions.length > 0;
  const checking = check.status === "checking";
  const preview = check.status === "ok" ? check.preview : null;
  const error = check.status === "error" ? check.error : null;
  // son başarılı önizleme denetim sürerken ve hata varken de görünür (ad uyarısı, sayılar)
  const shown = preview ?? lastPreview;
  const blocker = classifyBlockers(preview ?? error ?? (checking ? lastPreview : null), {
    hasGlobalCategory,
  });
  const rowErrs = rowErrors(error);
  // zincir üyelerinin üst sorunları kendi satırında; aynı satırda kod hatası da varsa ikisi listelenir
  const parentMsgs = parentProblemMessages(preview ?? (checking ? lastPreview : null), metric.id);
  const placed = placeRowErrors(mergeRowMessages(rowErrs, parentMsgs), metric.id, chain);
  const split = splitChain(chain, metric.id);
  // satıra düşmeyen hata asla sessiz pasif durum bırakmaz → genel hata satırı
  const genericError =
    error !== null && blocker === null && (rowErrs.size === 0 || placed.unplaced.length > 0)
      ? errorText(error)
      : props.commitError
        ? errorText(props.commitError)
        : null;
  const invalidIds = placed.order;
  const canPromote =
    !checking && preview !== null && blocker === null && rowErrs.size === 0 && !promoting;

  const rootErr = placed.rows.get(metric.id);
  const rootWarn = nameWarningFor(metric.id, shown);
  const parentBlocker = blocker?.kind === "parent" && !blocker.panel ? blocker : null;
  const panelBlocker = blocker?.panel ? blocker : null;
  const chosenNames = activePackages
    .filter((p) => form.packageIds.includes(p.id))
    .map((p) => p.name);

  const footer = (
    <>
      {invalidIds.length > 0 ? (
        <button
          type="button"
          style={{
            ...footerBtn(),
            marginRight: "auto",
            color: "var(--color-error)",
            borderColor: "color-mix(in srgb, var(--color-error) 30%, white)",
          }}
          onClick={props.onGoToRow}
        >
          {`${P.blockers(invalidIds.length)} — ${P.goToRow}`}
        </button>
      ) : null}
      <span role="status" aria-live="polite" style={{ ...muted, marginRight: 8 }}>
        {checking ? P.checking : ""}
      </span>
      <button type="button" style={footerBtn()} onClick={props.onClose}>
        {P.cancel}
      </button>
      <button
        type="button"
        style={{
          ...footerBtn(true),
          ...(canPromote ? {} : { opacity: 0.55, cursor: "not-allowed" }),
        }}
        disabled={!canPromote}
        onClick={props.onOpenConfirm}
      >
        {P.confirmAction}
      </button>
    </>
  );

  return (
    <KpiModal
      open={open}
      kicker={KPI_PACKAGES_COPY.page.kicker}
      title={P.title}
      description={`${metric.label} · ${metric.code}`}
      width={680}
      onClose={props.onClose}
      footer={footer}
    >
      <div className="grid gap-x-4" style={{ gridTemplateColumns: narrow ? "1fr" : "1fr 1fr" }}>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="promote-label">
            {P.labelField}
          </label>
          <input
            id="promote-label"
            style={inputStyle}
            value={form.label}
            placeholder={metric.label}
            maxLength={200}
            onKeyDown={blockEnter}
            onChange={(e) => props.onPatch({ label: e.target.value })}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor={promoteCodeInputId(metric.id)}>
            {P.codeField}
          </label>
          <input
            id={promoteCodeInputId(metric.id)}
            style={{
              ...inputStyle,
              ...monoStyle,
              ...(rootErr ? { borderColor: "var(--color-error)" } : {}),
            }}
            value={form.newCode}
            placeholder={form.defaultCode}
            maxLength={64}
            onKeyDown={blockEnter}
            {...(rootErr
              ? { "aria-invalid": true, "aria-describedby": promoteRowErrorId(metric.id) }
              : {})}
            onChange={(e) => props.onPatch({ newCode: e.target.value })}
          />
          {rootErr ? (
            <div id={promoteRowErrorId(metric.id)} style={{ ...errorBox, whiteSpace: "pre-line" }}>
              {rootErr}
            </div>
          ) : null}
        </div>
      </div>
      <div style={fieldStyle}>
        <span style={labelStyle} id="promote-parent-label">
          {P.parentField}
          {shown?.suggestedParentId && shown.suggestedParentId === form.parentId ? (
            <span style={{ ...muted, textTransform: "none", marginLeft: 6 }}>
              {KPI_PACKAGES_COPY.pills.suggested}
            </span>
          ) : null}
        </span>
        <SearchSelect
          id="promote-parent"
          ariaLabel={P.parentField}
          value={form.parentId}
          options={categoryOptions}
          emptyLabel={P.parentEmpty}
          placeholder={P.parentEmpty}
          showCode={false}
          onChange={(v) =>
            props.onPatch({ parentId: v, touched: { ...form.touched, parent: true } })
          }
        />
        {parentBlocker ? (
          <div
            id="promote-parent-msg"
            style={parentBlocker.text === P.parentRequired ? muted : errorBox}
          >
            {parentBlocker.text}
          </div>
        ) : null}
      </div>
      {rootWarn ? <div style={{ ...amberBox, marginBottom: 12 }}>{rootWarn}</div> : null}
      {(split.breakdownCount.get(metric.id) ?? 0) > 0 || placed.breakdown.has(metric.id) ? (
        <div style={{ marginBottom: 12 }} data-testid="promote-root-breakdown">
          <BreakdownSummary
            ownerId={metric.id}
            count={split.breakdownCount.get(metric.id) ?? 0}
            error={placed.breakdown.get(metric.id)}
          />
        </div>
      ) : null}

      <fieldset style={{ border: "none", margin: 0, padding: 0 }}>
        <legend style={sectionTitle}>{P.addToPackages}</legend>
        {activePackages.length === 0 ? (
          <div style={muted}>{P.packagesEmpty}</div>
        ) : (
          <div className="grid" style={{ gridTemplateColumns: narrow ? "1fr" : "1fr 1fr" }}>
            {activePackages.map((p) => (
              <label
                key={p.id}
                className="flex items-center gap-2 text-sm"
                style={{ minHeight: 44, color: "var(--color-text)" }}
              >
                <input
                  type="checkbox"
                  checked={form.packageIds.includes(p.id)}
                  onChange={(e) =>
                    props.onPatch({
                      packageIds: e.target.checked
                        ? [...form.packageIds, p.id]
                        : form.packageIds.filter((x) => x !== p.id),
                      touched: { ...form.touched, packages: true },
                    })
                  }
                />
                <span>{p.name}</span>
                {shown?.suggestedPackageIds.includes(p.id) ? (
                  <span style={muted}>{KPI_PACKAGES_COPY.pills.suggested}</span>
                ) : null}
              </label>
            ))}
          </div>
        )}
      </fieldset>

      <div style={{ marginTop: 16 }}>
        {panelBlocker ? (
          <div role="alert" style={errorBox} data-testid="promote-blocker">
            <div>{panelBlocker.text}</div>
            {panelBlocker.codes?.length ? (
              <div style={{ ...monoStyle, marginTop: 4 }}>
                {P.codesList(panelBlocker.codes.join(", "))}
              </div>
            ) : null}
            {panelBlocker.rows?.length ? (
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {panelBlocker.rows.map((id) => {
                  const r = chain.find((c) => c.metricId === id);
                  const m = r ? null : catalog.find((c) => c.id === id);
                  return (
                    <li key={id}>
                      {r ? `${r.label} (${r.code})` : m ? `${m.label} (${m.code})` : id}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        ) : split.deps.length === 0 ? (
          check.status === "checking" && chain.length === 0 ? null : (
            <div style={muted}>{P.noChain}</div>
          )
        ) : (
          <PromoteChainTable
            rows={split.deps}
            breakdownCount={split.breakdownCount}
            overrides={form.chainOverrides}
            rootParentId={form.parentId}
            categoryOptions={categoryOptions}
            errors={placed.rows}
            breakdownErrors={placed.breakdown}
            nameWarning={(id) => nameWarningFor(id, shown)}
            narrow={narrow}
            disabled={promoting}
            onChange={props.onPatchRow}
          />
        )}
        {genericError ? (
          <div role="alert" style={{ ...errorBox, marginTop: 8 }}>
            {genericError}
          </div>
        ) : null}
      </div>

      {shown ? (
        <section aria-label={P.previewTitle}>
          <h3 style={sectionTitle}>{P.previewTitle}</h3>
          <div className="text-sm" style={{ color: "var(--color-text-2)" }}>
            {P.rewrite(shown.setFormulas, shown.catalogFormulas, shown.unitTemplates)}
          </div>
          {chosenNames.length > 0 ? (
            <div className="text-sm" style={{ color: "var(--color-text-2)", marginTop: 4 }}>
              {`${P.addToPackages}: ${packageNamesText(chosenNames)}`}
            </div>
          ) : null}
        </section>
      ) : null}

      <div data-kpi-modal-confirm>
        <ConfirmDialog
          open={props.confirmOpen}
          title={P.confirmTitle}
          body={confirmText(Math.max(1, split.total))}
          confirmLabel={P.confirmAction}
          cancelLabel={P.cancel}
          tone="neutral"
          busy={promoting}
          busyLabel={P.promoting}
          onConfirm={props.onConfirm}
          onCancel={props.onCancelConfirm}
        />
      </div>
    </KpiModal>
  );
}
